/**
 * CollisionSystem — Circle-based collision detection
 *
 * Checks:
 * 1. Player bullets → enemies
 * 2. Enemy bullets → player
 * 3. Enemies → player (contact damage)
 * 4. Pickups → player
 *
 * Uses simple distance checks. For 200+ entities this is fine.
 * At 1000+ we'd add spatial hashing (deferred to Phase 5).
 */

export class CollisionSystem {
  constructor() {
    this._iframeDuration = 0.5; // seconds of invincibility after hit
    this._iframeTimer = 0;
  }

  update(dt, entities, engine) {
    // I-frame cooldown
    if (this._iframeTimer > 0) {
      this._iframeTimer -= dt;
    }

    const player = entities.queryTag('player')[0];
    if (!player) return;

    const playerPos = entities.getComponent(player, 'position');
    const playerRender = entities.getComponent(player, 'render');
    const playerRadius = playerRender ? playerRender.size : 12;

    // 1. Player bullets → enemies
    const bullets = entities.queryTag('playerBullet');
    const enemies = entities.queryTag('enemy');

    for (const bulletId of bullets) {
      if (!entities.isAlive(bulletId)) continue;
      const bPos = entities.getComponent(bulletId, 'position');
      const bDmg = entities.getComponent(bulletId, 'damage');
      const bRender = entities.getComponent(bulletId, 'render');
      const bulletPierce = entities.getComponent(bulletId, 'pierce');

      for (const enemyId of enemies) {
        if (!entities.isAlive(enemyId)) continue;
        const ePos = entities.getComponent(enemyId, 'position');
        const eRender = entities.getComponent(enemyId, 'render');
        const eHp = entities.getComponent(enemyId, 'health');

        const dist = this._dist(bPos, ePos);
        const hitDist = (bRender ? bRender.size : 4) + (eRender ? eRender.size : 12);

        if (dist < hitDist) {
          // Calculate damage
          let damage = bDmg ? bDmg.amount : 1;

          // Crit check
          if (engine.playerStats.critChance > 0 && Math.random() < engine.playerStats.critChance) {
            damage *= engine.playerStats.critMultiplier;
            engine.juice.flash('#ffcc00', 0.05);
          }

          eHp.current -= damage;

          // Hit particles
          engine.juice.burst(ePos.x, ePos.y, 6, bPos.angle || 0, 60, {
            color: eRender.color,
            speed: 150,
            size: 3,
            life: 0.3,
          });

          engine.audio.synthSfx('hit');
          engine.juice.shake(3, 0.08);

          if (eHp.current <= 0) {
            this._killEnemy(enemyId, entities, engine);
          }

          // Destroy bullet (unless piercing)
          if (!bulletPierce) {
            entities.destroy(bulletId);
            break; // this bullet is gone, check next
          }
        }
      }
    }

    // 2. Enemy bullets → player
    if (this._iframeTimer <= 0) {
      const enemyBullets = entities.queryTag('enemyBullet');
      for (const ebId of enemyBullets) {
        if (!entities.isAlive(ebId)) continue;
        const ebPos = entities.getComponent(ebId, 'position');
        const ebRender = entities.getComponent(ebId, 'render');
        const dist = this._dist(playerPos, ebPos);

        if (dist < playerRadius + (ebRender ? ebRender.size : 4)) {
          const ebDmg = entities.getComponent(ebId, 'damage');
          engine.takeDamage(ebDmg ? ebDmg.amount : 1);
          entities.destroy(ebId);
          this._iframeTimer = this._iframeDuration;
          engine.juice.flash('#ff0040', 0.1);
          engine.audio.synthSfx('hit');
          break; // only take one hit per frame
        }
      }
    }

    // 3. Enemy contact → player
    if (this._iframeTimer <= 0) {
      for (const enemyId of enemies) {
        if (!entities.isAlive(enemyId)) continue;
        const ePos = entities.getComponent(enemyId, 'position');
        const eRender = entities.getComponent(enemyId, 'render');
        const dist = this._dist(playerPos, ePos);

        if (dist < playerRadius + (eRender ? eRender.size : 12)) {
          engine.takeDamage(1);
          this._iframeTimer = this._iframeDuration;
          engine.juice.flash('#ff0040', 0.12);
          engine.juice.shake(8, 0.2);
          engine.audio.synthSfx('hit');
          break;
        }
      }
    }

    // 4. Pickups → player
    const pickups = entities.queryTag('pickup');
    for (const pickupId of pickups) {
      if (!entities.isAlive(pickupId)) continue;
      const pPos = entities.getComponent(pickupId, 'position');
      const dist = this._dist(playerPos, pPos);

      if (dist < engine.playerStats.pickupRadius) {
        const pickupData = entities.getComponent(pickupId, 'pickupData');
        if (pickupData) {
          if (pickupData.type === 'xp') {
            engine.addXP(pickupData.value);
            engine.audio.synthSfx('pickup');
          } else if (pickupData.type === 'heal') {
            engine.state.hp = Math.min(engine.state.maxHp, engine.state.hp + pickupData.value);
            engine.audio.synthSfx('pickup');
          }
        }
        entities.destroy(pickupId);
      }
    }
  }

  _killEnemy(enemyId, entities, engine) {
    const ePos = entities.getComponent(enemyId, 'position');
    const eRender = entities.getComponent(enemyId, 'render');
    const behavior = entities.getComponent(enemyId, 'behavior');

    // Death explosion particles
    engine.juice.emit(ePos.x, ePos.y, 15, {
      color: eRender ? eRender.color : '#ff0040',
      speed: 200,
      size: 4,
      life: 0.5,
      gravity: 100,
    });

    engine.juice.shake(5, 0.1);
    engine.juice.freeze(2);
    engine.audio.synthSfx('explode');

    // Score + kill
    engine.addScore(10);
    engine.addKill();

    // Drop XP gem
    const xpId = entities.create();
    entities.addComponent(xpId, 'position', { x: ePos.x, y: ePos.y, angle: 0 });
    entities.addComponent(xpId, 'velocity', {
      vx: (Math.random() - 0.5) * 40,
      vy: (Math.random() - 0.5) * 40,
    });
    entities.addComponent(xpId, 'render', {
      shape: 'diamond', size: 5, color: '#00ff41', glow: true,
    });
    entities.addComponent(xpId, 'pickupData', { type: 'xp', value: 5 });
    entities.addTag(xpId, 'pickup');

    // Splitter: spawn 2 smaller enemies on death
    if (behavior && behavior.type === 'split' && !behavior.isSplitChild) {
      for (let i = 0; i < 2; i++) {
        const splitId = entities.create();
        const angle = Math.random() * Math.PI * 2;
        entities.addComponent(splitId, 'position', {
          x: ePos.x + Math.cos(angle) * 20,
          y: ePos.y + Math.sin(angle) * 20,
          angle: 0,
        });
        entities.addComponent(splitId, 'velocity', { vx: 0, vy: 0 });
        entities.addComponent(splitId, 'health', { current: 1, max: 1 });
        entities.addComponent(splitId, 'render', {
          shape: eRender ? eRender.shape : 'triangle',
          size: (eRender ? eRender.size : 18) * 0.6,
          color: eRender ? eRender.color : '#00ff88',
          glow: true,
        });
        entities.addComponent(splitId, 'behavior', {
          type: 'chase',
          speed: (behavior.speed || 50) * 1.5,
          isSplitChild: true,
        });
        entities.addTag(splitId, 'enemy');
      }
    }

    // Chain lightning
    if (engine.playerStats.chainTargets > 0) {
      this._chainLightning(ePos, enemyId, entities, engine);
    }

    entities.destroy(enemyId);
  }

  _chainLightning(sourcePos, sourceId, entities, engine) {
    let currentPos = { ...sourcePos };
    let damage = engine.playerStats.damage;
    const hitIds = new Set([sourceId]);

    for (let i = 0; i < engine.playerStats.chainTargets; i++) {
      damage *= engine.playerStats.chainDamageDecay;
      const nearestEnemy = this._findNearest(currentPos, entities.queryTag('enemy'), entities, hitIds);
      if (!nearestEnemy) break;

      const ePos = entities.getComponent(nearestEnemy, 'position');
      const eHp = entities.getComponent(nearestEnemy, 'health');

      eHp.current -= damage;
      hitIds.add(nearestEnemy);

      engine.juice.burst(ePos.x, ePos.y, 4, 0, 360, {
        color: '#00ccff',
        speed: 100,
        size: 2,
        life: 0.2,
      });

      if (eHp.current <= 0) {
        this._killEnemy(nearestEnemy, entities, engine);
      }

      currentPos = { x: ePos.x, y: ePos.y };
    }
  }

  _findNearest(pos, candidates, entities, excludeIds) {
    let nearest = null;
    let nearestDist = Infinity;
    for (const id of candidates) {
      if (excludeIds.has(id)) continue;
      if (!entities.isAlive(id)) continue;
      const ePos = entities.getComponent(id, 'position');
      const d = this._dist(pos, ePos);
      if (d < nearestDist && d < 200) {
        nearest = id;
        nearestDist = d;
      }
    }
    return nearest;
  }

  _dist(a, b) {
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    return Math.sqrt(dx * dx + dy * dy);
  }
}
